import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import { resolveBkCountryWithTimeout } from '@bookkeeper/shared/bk-country';
import { mayRecordLandingSession } from '@/lib/replay-consent';
import { isLikelyBot } from '@bookkeeper/shared/bot-detection';
import { isSensitiveAnalyticsPathname, isProdCaptureHost } from '@bookkeeper/shared/capture-hosts';
import { isInternalMachine } from '@bookkeeper/shared/internal-machine';
import { AnalyticsEvents } from '@bookkeeper/shared/analytics-events';
import { CONSENT_STORED_EVENT, canRememberRefusal, getConsent } from '@bookkeeper/shared/consent';
import {
  captureFirstTouchUtms,
  clearFirstTouchUtms,
  persistFirstTouchUtmsAfterAccept,
} from '@bookkeeper/shared/first-touch-utm';
import {
  installSnapshotNetworkGate,
  isDoNotTrack,
  mayCaptureAddedSignals,
  parsePreconsentTelemetryMode,
  PRECONSENT_MAIN_CAPTURE_OPTIONS,
  PRECONSENT_REPLAY_SESSION_PROPERTY,
  registerGateInstance,
  snapshotMayLeave,
  withholdAddedSignals,
} from '@bookkeeper/shared/preconsent-telemetry';
import { markInitialCaptureReady, settleInitialCaptureReady } from '@/lib/initial-capture-ready';
import { scrubPii } from '@/lib/pii-scrubbing';
import { readPreconsentReplaySessionId, syncPreconsentReplay } from '@/lib/preconsent-replay';

// Config shape accepted by posthog.init(). Derived from the SDK signature
// because posthog-js does not re-export the underlying `PostHogConfig` type.
type PostHogInitConfig = NonNullable<Parameters<typeof posthog.init>[1]>;

let initialized = false;
let pendingConsent: 'opt_in' | 'restore_opt_in' | 'opt_out' | null = null;

/**
 * First-party ingest on www.bookeper.pl.
 *
 * F07 (2026-07-30) deleted the landing `/ingest` rewrite that proxied
 * *directly* to `eu.i.posthog.com` because a Vercel platform rewrite
 * forwards `Cookie` and the `Domain=.bookeper.pl` Supabase session rode
 * along to PostHog. That path stays forbidden.
 *
 * First-party ingest is restored by rewriting `/ingest/:path*` to
 * `https://app.bookeper.pl/ingest/:path*` — the Next Route Handler that
 * strips Cookie/Authorization by construction (F06). The browser still
 * talks same-origin to www; only our cookie-stripping proxy talks to
 * PostHog. A stale `VITE_POSTHOG_HOST=/ingest` is therefore correct again.
 */
const SAME_ORIGIN_INGEST_PROXY = '/ingest';

function resolveApiHost(): string {
  const configured = import.meta.env.VITE_POSTHOG_HOST;
  if (!configured) return SAME_ORIGIN_INGEST_PROXY;
  try {
    if (new URL(configured, window.location.origin).origin === window.location.origin) {
      return SAME_ORIGIN_INGEST_PROXY;
    }
  } catch {
    return SAME_ORIGIN_INGEST_PROXY;
  }
  return configured;
}

async function registerBkCountryBeforeInitialCapture(ph: {
  register: (properties: { bk_country: string }) => void;
}): Promise<void> {
  const properties = await resolveBkCountryWithTimeout();
  if (properties) ph.register(properties);
}

type PageviewClient = { capture: (event: string, properties?: Record<string, unknown>) => void };

function pathnameFromUrl(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return window.location.pathname;
  }
}

function capturePageview(ph: PageviewClient, url: string): void {
  if (!isSensitiveAnalyticsPathname(pathnameFromUrl(url))) {
    ph.capture('$pageview', { $current_url: `${window.location.origin}${pathnameFromUrl(url)}` });
  }
}

/**
 * The visitor's own consent decision, captured under the identity they clicked
 * with. Call it while the SDK is still opted out: under cookieless_mode
 * "on_reject" that is the cookieless anonymous mode their pageviews and clicks
 * were captured in, so the decision lands on the SAME session as the click.
 * After opt_in_capturing() the SDK switches to a cookie identity and a new
 * session id (2026-10-27 audit: consent events on different sessions than the
 * accept clicks), so the decision must not wait for the SDK's own `$opt_in`.
 */
function captureConsentDecision(
  ph: PageviewClient,
  event:
    typeof AnalyticsEvents.COOKIE_CONSENT_GIVEN | typeof AnalyticsEvents.COOKIE_CONSENT_DECLINED,
  preConsentSessionId?: string
): void {
  ph.capture(event, {
    source_surface: 'landing',
    ...(preConsentSessionId ? { pre_consent_session_id: preConsentSessionId } : {}),
  });
}

type SessionClient = { get_session_id?: () => string };

/**
 * The session id the visitor is on right now. opt_in_capturing() resets the SDK
 * and starts a NEW session, so an accept reads this first and stamps it on every
 * consent event as `pre_consent_session_id`: the click's session and the
 * post-opt-in session can be joined.
 */
function currentSessionId(ph: SessionClient): string | undefined {
  return ph.get_session_id?.() || undefined;
}

function startRecordingAfterAccept(
  ph: PageviewClient & {
    opt_in_capturing: (options?: {
      captureEventName?: false;
      captureProperties?: Record<string, string>;
    }) => void;
    startSessionRecording?: () => void;
  },
  url = window.location.href,
  restore = false,
  preConsentSessionId?: string
): void {
  ph.opt_in_capturing(
    restore
      ? { captureEventName: false }
      : preConsentSessionId
        ? { captureProperties: { pre_consent_session_id: preConsentSessionId } }
        : undefined
  );
  if (mayRecordLandingSession(getConsent()?.status)) ph.startSessionRecording?.();
  capturePageview(ph, url);
}

/**
 * Initialize PostHog for the landing app (www.bookeper.pl).
 *
 * Uses VITE_POSTHOG_KEY / VITE_POSTHOG_HOST env vars.
 * Respects GDPR: opt_out_capturing_by_default, enabled after cookie consent.
 * Shares identity with app.bookeper.pl via cross_subdomain_cookie.
 *
 * Pageview strategy: capture_pageview is disabled. The initial pageview (and
 * the page where consent is decided) is captured here / in posthogOptIn /
 * posthogOptOut; subsequent SPA navigations are captured by <PostHogPageview>
 * (src/components/PostHogPageview.tsx), which fires on route change only.
 *
 * Consent → capture mode (`cookieless_mode: "on_reject"`, verified against the
 * installed posthog-js 1.360.2 runtime):
 *   PENDING  (no choice yet) → cookieless anonymous capture (since 2026-07-15,
 *             founder-approved; implemented as an SDK-level opt-out at load)
 *   DECLINED (explicit opt-out) → cookieless anonymous capture (server-hash, no
 *             identify(), no PostHog tracking cookie)
 *   ACCEPTED → full capture (unchanged)
 */
export function initPostHog() {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  // Dev sessions must not pollute the production PostHog project. Hostname is
  // the signal (NODE_ENV/DEV misses prod builds served locally). Escape hatch
  // for deliberate instrumentation tests: VITE_POSTHOG_DEV_CAPTURE=true.
  const hostname = window.location.hostname;
  const isDevHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
  if (isDevHost && import.meta.env.VITE_POSTHOG_DEV_CAPTURE !== 'true') {
    markInitialCaptureReady(window.location.pathname);
    settleInitialCaptureReady();
    return;
  }

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  if (!apiKey) {
    if (import.meta.env.DEV) {
      console.warn('[PostHog] VITE_POSTHOG_KEY not set; skipping init');
    }
    markInitialCaptureReady(window.location.pathname);
    settleInitialCaptureReady();
    return;
  }

  // VITE_TELEMETRY_PRECONSENT_FULL (default OFF, lib/preconsent-replay.ts): explicit SDK-side
  // autocapture, rage/dead clicks, heatmaps, scroll depth and web vitals on this (cookieless before
  // consent) instance, plus a memory-only masked replay instance. Off: none of it is added.
  const preconsentFull =
    parsePreconsentTelemetryMode(import.meta.env.VITE_TELEMETRY_PRECONSENT_FULL) !== 'off';

  const config: PostHogInitConfig = {
    api_host: resolveApiHost(),
    ui_host: 'https://eu.posthog.com',
    // With the flag on, every event leaves INSIDE capture, where the consent check is made: no batch queue and no async
    // compression holds an accepted-era event until after a withdrawal (#9200 Astra r7), and XHR is the
    // transport the consent network gate holds whatever the SDK captured at load. Retries of a failed
    // request are held by that gate. Flag off: the SDK's own transport settings, as on main (#9200 ORCH r22).
    ...(preconsentFull
      ? { request_batching: false, disable_compression: true, api_transport: 'XHR' as const }
      : {}),
    opt_out_capturing_by_default: true,
    opt_out_capturing_persistence_type: 'cookie',
    // RODO: explicit decliners are captured cookielessly (anonymous, no cookie,
    // no identify()) rather than dropped — see this file's docstring for the
    // consent→mode matrix. NOTE: respect_dnt coerces Do-Not-Track to DENIED, so
    // DNT visitors are captured cookielessly even pre-choice (counsel decision
    // point — docs/operations/posthog-cookieless.md).
    cookieless_mode: 'on_reject',
    cross_subdomain_cookie: true,
    capture_performance: true,
    capture_pageleave: true,
    capture_pageview: false,
    // Cookieless $pageview is allowed under on_reject; session replay is not.
    // startSessionRecording() runs only after an explicit accept.
    disable_session_recording: true,
    respect_dnt: true,
    persistence: 'localStorage+cookie',
    ...(preconsentFull ? PRECONSENT_MAIN_CAPTURE_OPTIONS : {}),
    session_recording: {
      maskAllInputs: true,
      // Never a request/response header or body, whatever the project's remote config says.
      ...(preconsentFull ? { recordHeaders: false, recordBody: false } : {}),
      // Replay Meta.href is masked by this native hook, not before_send.
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: scrubPii(request.name.split(/[?#]/, 1)[0]) as string,
      }),
    },
    before_send: (event: CaptureResult | null): CaptureResult | null => {
      if (!event) return null;
      // The signals the full pre-consent flag adds are not covered by cookieless_mode "on_reject",
      // which still captures a decliner and a Do Not Track visitor: withhold them (#9200 Astra r2).
      if (preconsentFull) {
        event = withholdAddedSignals(
          event,
          mayCaptureAddedSignals(getConsent()?.status, isDoNotTrack())
        );
        if (!event) return null;
      }
      // Bot floor (2026-07-19): drop events from JS-executing crawlers before they
      // reach PostHog. Under cookieless_mode:"on_reject" each bot pageview otherwise
      // mints a fresh anonymous person (the server IP+UA hash can't stabilise), which
      // inflated organic-acquisition metrics on SEO landing pages. Real (including
      // founder-approved pending-consent) visitors are unaffected — see isLikelyBot.
      if (isLikelyBot()) return null;
      // Staging/preview floor (2026-07-22): only production surfaces may send
      // to the production PostHog project (staging + *.vercel.app previews
      // inherit the key). Local tests: VITE_POSTHOG_DEV_CAPTURE=true.
      if (
        !isProdCaptureHost(window.location.hostname) &&
        import.meta.env.VITE_POSTHOG_DEV_CAPTURE !== 'true'
      ) {
        return null;
      }
      // Internal-machine floor (2026-07-22): team members mark a browser once
      // via ?bkpr_internal=1 (clear with =0) — anonymous team browsing then
      // never reaches PostHog. Identified team sessions are already excluded
      // by the project's internal-user filter; this covers the anonymous side.
      if (isInternalMachine()) return null;
      event.properties = scrubPii(event.properties) as Record<string, unknown>;
      // Join key to the pre-consent recording (flag on only: nothing publishes it otherwise).
      const preconsentReplaySessionId = readPreconsentReplaySessionId();
      if (preconsentReplaySessionId) {
        event.properties[PRECONSENT_REPLAY_SESSION_PROPERTY] = preconsentReplaySessionId;
      }
      if (event.$set) {
        event.$set = scrubPii(event.$set) as Record<string, unknown>;
      }
      if (event.$set_once) {
        event.$set_once = scrubPii(event.$set_once) as Record<string, unknown>;
      }
      return event;
    },
    loaded: (ph) => {
      if (import.meta.env.DEV) {
        ph.debug();
      }
      captureFirstTouchUtms();

      // Restore consent from the shared cookie (returning users). A prior
      // explicit decline resumes cookieless capture under on_reject.
      //
      // capture_pageview:false disables native initial capture, including
      // opt_out_capturing(). This initializer owns exactly one manual view
      // after geo and the current stored decision have settled.
      // Accepted loads opt in before capturing; pending/declined loads first
      // establish native cookieless denial and never start replay.
      //
      // Session replay stays off on pending/declined (cookieless) loads —
      // disable_session_recording + no startSessionRecording.
      //
      // Register bk_country before that first $pageview so bounce visitors
      // still have a country on the only event they emit.
      const mountPathname = window.location.pathname;
      const mountUrl = window.location.href;
      return registerBkCountryBeforeInitialCapture(ph)
        .then(() => {
          ph.set_config({ capture_pageview: false });
          initialized = true;
          const consent = getConsent();
          // A queued accept is only a request to re-read the shared decision.
          // Another surface may have withdrawn it during the geo wait.
          if (pendingConsent !== 'opt_out' && consent?.status === 'accepted') {
            persistFirstTouchUtmsAfterAccept();
            // A decision made during the geo wait is recorded AFTER opting in: the
            // SDK is still in its pending default opt-out here (the explicit
            // opt_out_capturing() below never ran), and under cookieless_mode
            // "on_reject" posthog-js drops every capture while consent is pending
            // (is_capturing() = rejected || opted in). Nothing was captured before
            // load, so there is no cookieless session to keep the decision on.
            // A returning acceptance ('restore_opt_in' / none) is not new.
            const queuedAccept = pendingConsent === 'opt_in';
            startRecordingAfterAccept(ph, mountUrl, !queuedAccept);
            if (queuedAccept) {
              captureConsentDecision(ph, AnalyticsEvents.COOKIE_CONSENT_GIVEN);
            }
          } else {
            // Pending visitors keep session first-touch memory for signup
            // links. Decline / queued opt-out is the wipe.
            if (consent?.status === 'declined' || pendingConsent === 'opt_out') {
              clearFirstTouchUtms();
            }
            ph.opt_out_capturing();
            if (pendingConsent === 'opt_out') {
              captureConsentDecision(ph, AnalyticsEvents.COOKIE_CONSENT_DECLINED);
            }
            capturePageview(ph, mountUrl);
          }
          pendingConsent = null;
          // Capture the visited mount first, then the current route once if
          // geo delayed initialization across navigation. SPA waits on this barrier.
          const currentPathname = window.location.pathname;
          if (currentPathname !== mountPathname) capturePageview(ph, window.location.href);
          markInitialCaptureReady(mountPathname);
          markInitialCaptureReady(currentPathname);
        })
        .finally(() => {
          settleInitialCaptureReady();
        });
    },
  };

  // Before the SDK can make a request: a withdrawal must reach what the SDK still holds (#9200 Astra r7).
  // Only with the flag on: off, the gate would hold requests main sends (#9200 Sentry ycVg-).
  if (preconsentFull) {
    installSnapshotNetworkGate({
      apiHosts: [config.api_host],
      mayLeave: () => snapshotMayLeave(getConsent()?.status, isDoNotTrack()),
    });
    canRememberRefusal(); // once per page, only with the flag on; the send-time checks only read the answer (#9200 Cursor ybm4j)
  }
  posthog.init(apiKey, config);
  registerGateInstance(posthog); // the destinations the SDK resolved, not the config string
  // Flag on: request the recording at the first pageview, not after the banner (it starts when the
  // project's replay config answers). A no-op with the flag off.
  syncPreconsentReplay(getConsent()?.status, config);
  // A decision stored here or adopted from another tab (the shared owner announces both).
  window.addEventListener?.(CONSENT_STORED_EVENT, () => syncPreconsentReplay(getConsent()?.status));
}

/**
 * Call after user accepts cookies to enable PostHog capturing.
 * Also captures the current pageview that was suppressed before consent.
 */
export function posthogOptIn() {
  if (getConsent()?.status !== 'accepted') return;
  // The main instance records from here on; the pre-consent one stops.
  syncPreconsentReplay('accepted');
  persistFirstTouchUtmsAfterAccept();
  if (initialized) {
    // Order is the contract: record the decision while still cookieless, then
    // switch identity (captureConsentDecision docstring).
    const preConsentSessionId = currentSessionId(posthog);
    captureConsentDecision(posthog, AnalyticsEvents.COOKIE_CONSENT_GIVEN, preConsentSessionId);
    startRecordingAfterAccept(posthog, window.location.href, false, preConsentSessionId);
  } else {
    pendingConsent = 'opt_in';
  }
}

/**
 * Restore another tab's saved acceptance without a new consent event.
 * After SDK load there is no extra pageview. Before load, the queued restore
 * captures the one initial pageview when loading finishes.
 */
export function posthogRestoreConsent() {
  syncPreconsentReplay('accepted');
  if (initialized) {
    posthog.opt_in_capturing({ captureEventName: false });
    if (mayRecordLandingSession(getConsent()?.status)) posthog.startSessionRecording();
  } else {
    pendingConsent = 'restore_opt_in';
  }
}

/**
 * Ids for the www → app hand-off, computed at click time (the session id can
 * rotate on opt-in or after 30 idle minutes), or null before the SDK loads.
 *
 * - `sessionId` (`ph_sid`): always, pending / declined / accepted alike. Before
 *   consent the SDK still holds a random in-memory session id; it travels only
 *   in the URL to our own subdomain and nothing is written to the device. The
 *   app bootstraps its session from it so www and app share `$session_id`.
 * - `distinctId` (`ph_did`): only for an ACCEPTED visitor in cookie mode, and
 *   never the cookieless sentinel (`$posthog_cookieless`, any `$`-prefixed id).
 */
export function getPosthogHandoffIds(): {
  distinctId: string | null;
  sessionId: string | null;
} | null {
  if (!initialized) return null;
  const sessionId = posthog.get_session_id() || null;
  let distinctId: string | null = null;
  if (getConsent()?.status === 'accepted' && !posthog.has_opted_out_capturing()) {
    const id = posthog.get_distinct_id();
    if (id && !id.startsWith('$')) distinctId = id;
  }
  return sessionId || distinctId ? { distinctId, sessionId } : null;
}

/**
 * Call after the user declines cookies. Under `cookieless_mode: "on_reject"`
 * this puts PostHog into cookieless (anonymous, no-cookie) capture rather than
 * dropping the visitor entirely. The initializer already owns the initial
 * manual pageview; a later decline changes mode without duplicating it.
 */
export function posthogOptOut() {
  clearFirstTouchUtms();
  // A refusal stops the pre-consent recording in every mode (see preconsent-replay.ts).
  syncPreconsentReplay('declined');
  if (initialized) {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
    // Now opted out = cookieless: the decline is captured without a cookie id.
    captureConsentDecision(posthog, AnalyticsEvents.COOKIE_CONSENT_DECLINED);
  } else {
    pendingConsent = 'opt_out';
  }
}
